/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";

import { Log } from "../../../model/logging";
import {
  SignInApi,
  SignInFlow,
  SignInFlowEvent,
  SignInLogger,
  SignInResult
} from "../../../model/logging/SignInLogger";
import { SignInInteractor } from "../../../model/interactor";
import type { ValidateEmailTokenResult } from "../../../model/interfaces/ISignInInteractor";
import { DiagnosticSeverity } from "../../../model/types";
import {
  extractServiceErrorMessage,
  localizedErrorFallback
} from "../../../model/utility";
import { LocaleKeys } from "../../../model/locale";
import type { UnityRootState } from "../../UnityAppStore";
import type { ResetIdentityPayload } from "../signIn.types";

const TAG: string = "signIn/passwordResetThunks";

const FLOW_FAILED_MESSAGE: string = "Password reset flow failed";

/**
 * Emits the flow_failed diagnostic for a password-reset step.
 *
 * The email address and the reset token are user identifiers and secrets
 * respectively, so neither the values nor the service message are recorded.
 */
function logPasswordResetFailure(stepReached: string, error: unknown): void {
  SignInLogger.logFlowFailure(
    SignInFlowEvent.FlowFailed,
    FLOW_FAILED_MESSAGE,
    error,
    { flow: SignInFlow.PasswordReset, step_reached: stepReached }
  );
  if (stepReached === SignInApi.UpdatePassword) {
    SignInLogger.logPasswordResetCompleted({
      status: SignInResult.Failure,
      error_code: SignInLogger.describeErrorType(error)
    });
  }
}

/**
 * Forgot password — Step 1: emails the user a password reset link.
 */
export const sendForgotPasswordLink = createAsyncThunk<
  string,
  { email: string; signal?: AbortSignal },
  { state: UnityRootState; rejectValue: string }
>(
  "signIn/sendForgotPasswordLink",
  async (
    params: { email: string; signal?: AbortSignal },
    { rejectWithValue }
  ) => {
    try {
      const email: string = params.email.trim();
      if (email.length === 0) {
        return rejectWithValue(
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_RECOVERY_EMAIL_REQUIRED_MESSAGE
          )
        );
      }

      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowStarted,
        "Password reset flow started",
        DiagnosticSeverity.Info,
        { flow: SignInFlow.PasswordReset }
      );
      // Never the email address itself — only that a reset was requested.
      SignInLogger.logPasswordResetRequested();

      const signInInteractor: SignInInteractor = new SignInInteractor();

      await signInInteractor.forgotPassword(email, params.signal);

      return email;
    } catch (error: unknown) {
      logPasswordResetFailure(SignInApi.ForgotPassword, error);
      Log.e(TAG, "sendForgotPasswordLink()", error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_LINK_SEND_ERROR_MESSAGE
          )
        )
      );
    }
  }
);

/**
 * Forgot password — Step 2: validates the token from the emailed link and
 * returns the identity the new password will be set on.
 */
export const validateResetEmailToken = createAsyncThunk<
  ResetIdentityPayload,
  { emailToken: string; signal?: AbortSignal },
  { state: UnityRootState; rejectValue: string }
>(
  "signIn/validateResetEmailToken",
  async (
    params: { emailToken: string; signal?: AbortSignal },
    { rejectWithValue }
  ) => {
    try {
      const emailToken: string = params.emailToken.trim();
      if (emailToken.length === 0) {
        return rejectWithValue(
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_LINK_INVALID_ERROR_MESSAGE
          )
        );
      }

      const signInInteractor: SignInInteractor = new SignInInteractor();

      const response: ValidateEmailTokenResult =
        await signInInteractor.validateEmailToken(emailToken, params.signal);

      return {
        partnerCustomerId: response.partnerCustomerId,
        identityId: response.identityId,
        msoName: response.msoName,
        email: response.email
      };
    } catch (error: unknown) {
      logPasswordResetFailure(SignInApi.ValidateEmailToken, error);
      Log.e(TAG, "validateResetEmailToken()", error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_LINK_INVALID_ERROR_MESSAGE
          )
        )
      );
    }
  }
);

/**
 * Forgot password — Step 3: sets the new password on the validated identity.
 */
export const submitUpdatedPassword = createAsyncThunk<
  void,
  { password: string; signal?: AbortSignal },
  { state: UnityRootState; rejectValue: string }
>(
  "signIn/submitUpdatedPassword",
  async (
    params: { password: string; signal?: AbortSignal },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: UnityRootState = getState();
      const identityPayload: ResetIdentityPayload | null =
        state.signIn.resetIdentityPayload !== undefined
          ? state.signIn.resetIdentityPayload
          : null;

      if (identityPayload === null) {
        return rejectWithValue(
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_SESSION_INVALID_MESSAGE
          )
        );
      }

      const signInInteractor: SignInInteractor = new SignInInteractor();

      await signInInteractor.updatePassword(
        {
          partnerCustomerId: identityPayload.partnerCustomerId,
          identityId: identityPayload.identityId,
          msoName: identityPayload.msoName,
          email: identityPayload.email
        },
        params.password,
        params.signal
      );

      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowCompleted,
        "Password reset flow completed",
        DiagnosticSeverity.Info,
        { flow: SignInFlow.PasswordReset }
      );
      SignInLogger.logPasswordResetCompleted({
        status: SignInResult.Success
      });
    } catch (error: unknown) {
      logPasswordResetFailure(SignInApi.UpdatePassword, error);
      Log.e(TAG, "submitUpdatedPassword()", error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_PASSWORD_UPDATE_ERROR_MESSAGE
          )
        )
      );
    }
  }
);
